import { ScatterChart } from "@get-dx/d3-charts";
import { setQueryStringParam, objectToQueryString } from "../../shared/utils";
import { debounce } from "../../lib/utils";
import * as d3 from "d3";
import { chartTooltipHtml } from "../chartTooltipHtml";

const CHART_TYPES = [
  { name: "Speed", slug: "speed" },
  { name: "Ease of delivery", slug: "ease" },
  { name: "Quality", slug: "reliability" },
  { name: "Weekly time loss", slug: "time_loss" },
];

const CUSTOM_DRIVER_BASELINE = 50;

const DATA_TYPES = [
  { name: "Drivers", slug: "drivers" },
  { name: "Workflows", slug: "workflows" },
  { name: "CSAT", slug: "csat" },
];

/**
 * Impact Performance Matrix Analysis Chart
 *
 * Displays correlation analysis between different data types (drivers, workflows, CSAT)
 * and outcome metrics (speed, ease, reliability, time loss) in a scatter plot matrix.
 *
 * The chart plots:
 * - X-axis: Correlation strength with selected outcome metric
 * - Y-axis: Performance level (% favorable/meeting target - 50)
 *
 * Supports three data types:
 * - Drivers: Regular survey factors (% favorable)
 * - Workflows: Behavioral questions (% meeting target)
 * - CSAT: Internal tool satisfaction (% satisfied)
 */
export function keyDriversChart(args) {
  const {
    baseUrl,
    workflowBaseUrl,
    csatBaseUrl,
    statusUrl,
    minResponseThreshold,
  } = args;

  // Map data types to their corresponding API endpoints
  const dataTypeUrls = {
    drivers: baseUrl,
    workflows: workflowBaseUrl,
    csat: csatBaseUrl,
  };

  const getValidChartTypeSlug = (metric) => {
    return CHART_TYPES.some((chart) => chart.slug === metric)
      ? metric
      : CHART_TYPES[0].slug;
  };

  const getValidDataTypeSlug = (dataType) => {
    return DATA_TYPES.some((type) => type.slug === dataType)
      ? dataType
      : DATA_TYPES[0].slug;
  };

  const queryParams = new URLSearchParams(window.location.search);
  const metricParam = queryParams.get("metric");
  const dataTypeParam = queryParams.get("data_type");
  const chartTypeSlug = getValidChartTypeSlug(metricParam);
  const dataTypeSlug = getValidDataTypeSlug(dataTypeParam);

  // Update URL only if the original metric param was invalid
  if (metricParam !== chartTypeSlug) {
    const url = new URL(window.location);
    url.searchParams.set("metric", chartTypeSlug);
    window.history.replaceState({}, "", url);
  }

  // Update URL only if the original data_type param was invalid
  if (dataTypeParam !== dataTypeSlug) {
    const url = new URL(window.location);
    url.searchParams.set("data_type", dataTypeSlug);
    window.history.replaceState({}, "", url);
  }

  return {
    isHidden: false,
    chartTypes: CHART_TYPES,
    dataTypes: DATA_TYPES,
    chartTypeSlug,
    dataTypeSlug,
    baseUrl,
    workflowBaseUrl,
    valuesToImprove: [],
    correlations: null,
    latestData: null,
    jobId: null,
    jobStatus: null,
    pollTimer: null,
    pollAttempts: 0,
    maxPollAttempts: 20,
    pollIntervalMs: 2000,
    isLoading: false,
    activeFetchController: null,
    minResponseThreshold,
    chartError: false,
    chartInstance: null,
    debouncedResizeHandler: null,
    init() {
      if (!this.isHidden) {
        this.refreshChartData();
      }

      this.handleKpiResults = (event) => {
        const results = event?.detail?.kpiResults || [];

        if (results.length === 0) {
          this.abortActiveFetch();
          this.clearChart();
        }
      };
      window.removeEventListener("kpiResultsFetched", this.handleKpiResults);
      window.addEventListener("kpiResultsFetched", this.handleKpiResults);
    },
    destroy() {
      if (this.handleKpiResults) {
        window.removeEventListener("kpiResultsFetched", this.handleKpiResults);
      }
      // Clean up chart resize listener
      if (this.chartInstance && this.debouncedResizeHandler) {
        window.removeEventListener("resize", this.chartInstance.resize);
        window.removeEventListener("resize", this.debouncedResizeHandler);
      }
    },
    get squadIds() {
      return this.$store?.snapshotResultFilters?.squadIds || [];
    },
    get groupIds() {
      return this.$store?.snapshotResultFilters?.groupIds || [];
    },
    get tagIds() {
      return this.$store?.snapshotResultFilters?.tagIds || [];
    },
    updateFilter(filterType, ids, paramName, options = {}) {
      const normalizedIds = Array.isArray(ids) ? ids : [];

      if (this.$store?.snapshotResultFilters) {
        this.$store.snapshotResultFilters[filterType] = [...normalizedIds];

        // Clear other filters if needed
        if (options.clearSquads) {
          this.$store.snapshotResultFilters.squadIds = [];
        }
        if (options.clearGroups) {
          this.$store.snapshotResultFilters.groupIds = [];
        }
        if (options.filterType) {
          this.$store.snapshotResultFilters.filterType = options.filterType;
        }
      }

      setQueryStringParam(
        paramName,
        normalizedIds.length ? normalizedIds.join(",") : null,
      );

      // Clear related params
      if (options.clearSquads) {
        setQueryStringParam("squad_ids", null);
      }
      if (options.clearGroups) {
        setQueryStringParam("group_ids", null);
      }

      this.refreshChartData();
    },
    changeSquadIds(squadIds) {
      this.updateFilter("squadIds", squadIds, "squad_ids", {
        clearGroups: true,
        filterType: "teams",
      });
    },
    changeTagIds(tagIds) {
      this.updateFilter("tagIds", tagIds, "tag_ids");
    },
    changeGroupIds(groupIds) {
      this.updateFilter("groupIds", groupIds, "group_ids", {
        clearSquads: true,
        filterType: "groups",
      });
    },
    get chartTypeName() {
      const selectedChart = this.chartTypes.find(
        (chart) => chart.slug === this.chartTypeSlug,
      );

      return selectedChart.name;
    },
    get dataTypeName() {
      const selectedType = this.dataTypes.find(
        (type) => type.slug === this.dataTypeSlug,
      );

      return selectedType.name;
    },
    chartName(chartItem) {
      return chartItem.name;
    },
    selectChart(chartTypeSlug) {
      this.chartTypeSlug = chartTypeSlug;

      // Update url query param without reloading the page
      const url = new URL(window.location);
      url.searchParams.set("metric", chartTypeSlug);
      window.history.pushState({}, "", url);

      this.refreshChartData();
    },
    selectDataType(dataTypeSlug) {
      this.dataTypeSlug = dataTypeSlug;

      // Update url query param without reloading the page
      const url = new URL(window.location);
      url.searchParams.set("data_type", dataTypeSlug);
      window.history.pushState({}, "", url);

      this.refreshChartData();
    },
    async fetchResultItems(signal) {
      const paramsObj = {
        branch: this.$store?.snapshotResultFilters?.branch,
      };

      if (this.squadIds.length) {
        paramsObj.squad_ids = this.squadIds.join(",");
      }

      if (this.groupIds.length) {
        paramsObj.group_ids = this.groupIds.join(",");
      }

      if (this.tagIds.length) {
        paramsObj.tag_ids = this.tagIds.join(",");
      }

      const queryParams = objectToQueryString(paramsObj);

      // Choose URL based on data type
      const fetchUrl = dataTypeUrls[this.dataTypeSlug];

      // Validate that required URL is present
      if (!fetchUrl) {
        throw new Error(
          `keyDriversChart: Missing URL for data type '${this.dataTypeSlug}'. Please provide the corresponding URL in the chart configuration.`,
        );
      }

      const url = `${fetchUrl}?${queryParams}`;

      const resp = await fetch(url, { signal });
      return resp.json();
    },
    async fetchJobStatus() {
      if (!statusUrl || !this.jobId) return null;

      const url = new URL(statusUrl, window.location.origin);
      url.searchParams.set("job_id", this.jobId);

      const resp = await fetch(url.toString());
      return resp.json();
    },
    updateVisibility(resultItems) {
      const highestResponseCount = Array.isArray(resultItems)
        ? resultItems.reduce((max, item) => {
            return item.count > max ? item.count : max;
          }, -Infinity)
        : -Infinity;

      this.isHidden = highestResponseCount < minResponseThreshold;
    },
    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    resetPollingState() {
      this.stopPolling();
      this.pollAttempts = 0;
      this.jobId = null;
      this.jobStatus = null;
    },
    abortActiveFetch() {
      if (this.activeFetchController) {
        this.activeFetchController.abort();
        this.activeFetchController = null;
      }
    },
    clearChart() {
      this.abortActiveFetch();
      this.resetPollingState();
      this.latestData = null;
      this.isHidden = true;
      this.isLoading = false;
      this.clearChartElement();
    },
    schedulePoll() {
      if (!statusUrl || !this.jobId) return;

      this.stopPolling();

      if (this.pollAttempts >= this.maxPollAttempts) {
        this.handleError("Timed out waiting for driver analysis results");
        return;
      }

      this.pollTimer = setTimeout(() => {
        this.pollJobStatus();
      }, this.pollIntervalMs);
    },
    async pollJobStatus() {
      if (!this.jobId) return;

      this.pollAttempts += 1;

      try {
        const data = await this.fetchJobStatus();
        this.handleResponseData(data, { isPolling: true });
      } catch (error) {
        console.error("Failed to poll driver analysis status", error);
        this.handleError("Unable to fetch driver analysis status");
      }
    },
    handleError(message) {
      this.stopPolling();
      this.jobStatus = "error";
      this.chartError = true;
      this.isHidden = false;
      this.latestData = null;
      this.isLoading = false;
      this.clearChartElement();
      console.error(message);
    },
    validateResponseStatus(data) {
      if (data.status === undefined || data.status === null) {
        const jobIdInfo = data.job_id ? ` (job_id: ${data.job_id})` : "";
        this.jobId = data.job_id || null;
        this.handleError(
          `Driver analysis returned invalid status${jobIdInfo}. Received: ${JSON.stringify(data)}`,
        );
        return false;
      }
      return true;
    },
    clearChartElement() {
      const chartElement = document.getElementById("key-drivers-chart");
      if (chartElement) chartElement.innerHTML = "";
    },
    handleCompleteStatus(data) {
      this.stopPolling();
      this.jobStatus = "complete";
      this.jobId = null;
      this.chartError = false;
      this.correlations = data.correlations || [];
      this.latestData = data;
      this.updateVisibility(data.result_items);

      if (this.isHidden) {
        this.clearChartElement();
      } else {
        this.renderChart();
      }

      this.isLoading = false;
    },
    handleErrorStatus(data) {
      this.jobId = data.job_id || null;
      this.handleError(data.error || "Driver analysis failed");
    },
    handlePendingStatus(data, isPolling) {
      this.jobId = data.job_id || null;
      this.jobStatus = data.status;
      this.isLoading = true;

      if (!isPolling) {
        this.pollAttempts = 0;
      }

      this.schedulePoll();
    },
    handleResponseData(data, { isPolling = false } = {}) {
      if (!data || !this.validateResponseStatus(data)) return;

      const status = data.status;

      if (status === "complete") return this.handleCompleteStatus(data);
      if (status === "error") return this.handleErrorStatus(data);

      return this.handlePendingStatus(data, isPolling);
    },
    async refreshChartData() {
      this.resetPollingState();
      this.latestData = null;
      this.chartError = false;
      this.isLoading = true;

      const controller = new AbortController();
      this.activeFetchController = controller;

      try {
        const data = await this.fetchResultItems(controller.signal);

        if (this.activeFetchController !== controller) {
          return;
        }

        this.activeFetchController = null;
        this.handleResponseData(data);
      } catch (error) {
        if (this.activeFetchController !== controller) {
          return;
        }

        this.activeFetchController = null;

        if (error.name === "AbortError") {
          return;
        }

        console.error("Failed to fetch driver analysis", error);
        this.handleError("Unable to fetch driver analysis");
      }
    },
    prepareChartData() {
      const correlations = this.correlations || [];
      const resultItems = this.latestData?.result_items || [];

      if (!Array.isArray(correlations) || correlations.length === 0) {
        return null;
      }

      // For workflows, correlations are keyed by behavioral_question_id
      // For drivers, correlations are keyed by factor_id
      // For CSAT, correlations are keyed by internal_tool_id
      const idFieldMap = {
        workflows: "behavioral_question_id",
        csat: "internal_tool_id",
        drivers: "factor_id",
      };
      const idField = idFieldMap[this.dataTypeSlug] || idFieldMap.drivers;

      const correlationsMap = new Map(
        correlations.map((x) => [
          x[idField],
          {
            ease: x.ease,
            speed: x.speed,
            reliability: x.reliability,
            time_loss: x.time_loss,
          },
        ]),
      );

      const values = resultItems
        .filter((item) => !item.outcome && correlationsMap.has(item.id))
        .map((item) => {
          const correlationValue = correlationsMap.get(item.id)[
            this.chartTypeSlug
          ];

          const yValue =
            item.vs_75th != null && item.vs_75th != undefined
              ? parseFloat(item.vs_75th)
              : parseFloat(item.score) - CUSTOM_DRIVER_BASELINE;

          return {
            x: correlationValue,
            y: yValue,
            driver: item.name,
            prompt: item.prompt,
            count: item.count,
            sample_size: item.sample_size,
            one_count: item.one_count,
            two_count: item.two_count,
            three_count: item.three_count,
            four_count: item.four_count,
            five_count: item.five_count,
            zero_position_count: item.zero_position_count,
            one_position_count: item.one_position_count,
            two_position_count: item.two_position_count,
            three_position_count: item.three_position_count,
            four_position_count: item.four_position_count,
            five_position_count: item.five_position_count,
            six_position_count: item.six_position_count,
            scale_points: item.scale_points,
            slug: item.slug,
            unit: item.unit,
            lower_value_is_better: item.lower_value_is_better,
            options: item.options,
          };
        });

      return values.length > 0 ? values : null;
    },
    renderChart() {
      if (this.isHidden || !this.latestData) return;

      const chartElement = document.getElementById("key-drivers-chart");
      if (!chartElement) return;

      const values = this.prepareChartData();

      if (!values) {
        this.valuesToImprove = [];
        this.isHidden = true;
        this.isLoading = false;
        return;
      }

      // Find the 5 drivers with the highest importance
      this.valuesToImprove = [...values]
        .sort((a, b) => d3.descending(a.x, b.x))
        .slice(0, 5);

      let [xMin, xMax] = d3.extent(values.map((d) => d.x));
      let [yMin, yMax] = d3.extent(values.map((d) => d.y));

      // Buffer the charts so no points are plotted on the axis lines
      const xBuffer = (Math.abs(xMax + xMin) / 2) * 0.08;
      const yBuffer = (Math.abs(yMax + yMin) / 2) * 0.1;

      xMax += xBuffer * 5;
      yMax += yBuffer * 5;
      xMin -= xBuffer;
      yMin -= yBuffer * 5;

      const xMid = (xMax + xMin) / 2;
      const yMid = (yMax + yMin) / 2;

      // Clear existing chart before we render
      chartElement.innerHTML = "";

      const tooltipMap = {
        workflows: chartTooltipHtml.keyDriversWorkflow,
        csat: chartTooltipHtml.keyDriversCsat,
        drivers: chartTooltipHtml.keyDrivers,
      };

      const chart = new ScatterChart({
        elChart: chartElement,
        xAxisTickLabelFormat: () => "",
        yAxisTickLabelFormat: () => "",
        matrix: [xMid, yMid],
        dotRadius: 3,
        highlightedQuadrant: 4,
        showYAxisTicks: false,
        showXAxisTicks: false,
        showXAxisTickLabels: false,
        showYAxisTickLabels: false,
        axis: {
          x: {
            label: "Correlation",
            min: xMin,
            max: xMax,
          },
          y: {
            label: "Performance",
            min: yMin,
            max: yMax,
          },
        },
        values,
        labelTextFormat: (d) => d.driver,
        tooltipHtml: tooltipMap[this.dataTypeSlug] || tooltipMap.drivers,
      });

      chart.redraw();
      this.isLoading = false;

      // Adjust label positioning to prevent overflow
      this.adjustLabelPositions(chartElement);

      // Remove the library's default resize listener and add debounced version
      if (this.chartInstance && this.debouncedResizeHandler) {
        window.removeEventListener("resize", this.chartInstance.resize);
        window.removeEventListener("resize", this.debouncedResizeHandler);
      }

      this.chartInstance = chart;

      // Create debounced resize handler that calls chart.resize and adjusts labels
      this.debouncedResizeHandler = debounce(() => {
        if (this.chartInstance) {
          this.chartInstance.resize();
          this.adjustLabelPositions(chartElement);
        }
      }, 250);

      // Remove library's automatic resize listener
      window.removeEventListener("resize", chart.resize);

      // Add our debounced resize listener
      window.addEventListener("resize", this.debouncedResizeHandler);
    },
    adjustLabelPositions(chartElement) {
      // Wait for next frame to ensure chart has rendered
      requestAnimationFrame(() => {
        const labels = chartElement.querySelectorAll(".dot-label-text");
        const chartRect = chartElement.getBoundingClientRect();

        labels.forEach((label) => {
          const labelRect = label.getBoundingClientRect();

          // Check if label extends past right edge of chart
          if (labelRect.right > chartRect.right) {
            // Anchor text to the right instead of left
            label.setAttribute("text-anchor", "end");
            // Adjust x position to be to the left of the dot instead of right
            const currentX = parseFloat(label.getAttribute("x"));
            label.setAttribute("x", currentX - 10); // Move left by offset + padding
          }
        });
      });
    },
  };
}
